import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db/index.js';
import { deckPath, deleteDeck, restoreDeck, descendantIds } from '../../db/repo/decks.js';
import { useDeckOverview } from '../../hooks/useDecks.js';
import { createLocalSnapshot } from '../../backup/localSnapshots.js';
import PageHeader from '../../components/PageHeader.jsx';
import Button from '../../components/Button.jsx';
import Sheet from '../../components/Sheet.jsx';
import EmptyState from '../../components/EmptyState.jsx';
import { ActionList, ActionRow, ActionTile, TileGrid } from '../../components/ActionList.jsx';
import { toast } from '../../components/Toaster.jsx';
import DeckTree from './DeckTree.jsx';
import DeckFormSheet from './DeckFormSheet.jsx';
import DeckOptionsSheet from './DeckOptionsSheet.jsx';
import CustomStudySheet from './CustomStudySheet.jsx';
import { TodayCard } from './TodayCard.jsx';
import HelpButton from '../../components/HelpButton.jsx';
import { useMediaQuery } from '../../hooks/useMediaQuery.js';
import styles from './Decks.module.scss';

function useDeckStats(deckIds) {
  const key = deckIds.join(',');
  return useLiveQuery(
    async () => {
      if (!deckIds.length) return null;
      const cards = (await db.cards.where('deckId').anyOf(deckIds).toArray()).filter((c) => !c.deletedAt);
      return {
        cards: cards.length,
        known: cards.filter((c) => (c.fsrs?.scheduledDays ?? 0) >= 21).length,
        unseen: cards.filter((c) => !c.fsrs).length,
        paused: cards.filter((c) => c.suspended).length,
        flagged: cards.filter((c) => c.flag).length,
      };
    },
    [key],
    null,
  );
}

export default function DeckPage() {
  const { deckId } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const overview = useDeckOverview();
  const [sheet, setSheet] = useState(params.get('custom') ? 'custom' : null);

  const deck = overview?.decks.find((d) => d.id === deckId);
  const node = useMemo(() => {
    const find = (nodes) => {
      for (const n of nodes) {
        if (n.id === deckId) return n;
        const f = find(n.children);
        if (f) return f;
      }
      return null;
    };
    return overview ? find(overview.tree) : null;
  }, [overview, deckId]);
  const subtree = useMemo(() => (overview && deck ? descendantIds(overview.decks, deck.id) : []), [overview, deck]);
  const stats = useDeckStats(subtree);
  const isDesktop = useMediaQuery('(min-width: 1100px)');

  if (!overview) return null;
  if (!deck || deck.deletedAt) {
    return (
      <div className={styles.page}>
        <PageHeader title={deck?.deletedAt ? 'Deck deleted' : 'Deck not found'} back="/" />
        <div className={styles.body}>
          <EmptyState
            icon={deck?.deletedAt ? 'trash' : 'decks'}
            title={deck?.deletedAt ? 'This deck is in the trash' : 'This deck doesn’t exist'}
            action={
              deck?.deletedAt ? (
                <Button variant="primary" icon="restore" onClick={() => restoreDeck(db, deck.id)}>
                  Restore deck
                </Button>
              ) : (
                <Button variant="primary" to="/">
                  All decks
                </Button>
              )
            }
          >
            {deck?.deletedAt ? 'Restore it to bring back all of its cards and study history.' : null}
          </EmptyState>
        </div>
      </div>
    );
  }

  const counts = overview.counts.get(deck.id) ?? { new: 0, learn: 0, review: 0 };
  const parentPath = deck.parentId ? deckPath(overview.decks, deck.parentId) : null;
  const close = () => {
    setSheet(null);
    if (params.get('custom')) setParams({}, { replace: true });
  };

  const remove = async () => {
    await createLocalSnapshot(db, 'before-delete');
    const res = await deleteDeck(db, deck.id);
    close();
    navigate('/');
    toast.info(`Deleted “${deck.name}” and ${res.notes} note${res.notes === 1 ? '' : 's'}`, {
      duration: 8000,
      action: { label: 'Undo', onClick: () => restoreDeck(db, deck.id) },
    });
  };

  const isEmpty = stats && stats.cards === 0;

  return (
    <div className={styles.page}>
      <PageHeader
        title={deck.name}
        subtitle={parentPath ? `Inside ${parentPath}` : null}
        back="/"
        actions={
          <>
            <HelpButton topic="decks" />
            <Button iconOnly icon="more" onClick={() => setSheet('menu')}>
              Deck options
            </Button>
          </>
        }
      />
      <div className={styles.body}>
        <div className={styles.deckSplit}>
          <div className={styles.deckMain}>
            {isEmpty ? (
              <EmptyState
                icon="plus"
                title="This deck is empty"
                action={
                  <Button variant="primary" size="lg" icon="plus" to={`/add?deck=${deck.id}`}>
                    Add your first card
                  </Button>
                }
              >
                Add a few cards, then come back here to study them.
              </EmptyState>
            ) : (
              <TodayCard
                counts={counts}
                to={`/study/${deck.id}`}
                title="Ready to study"
                extra={
                  <Button size="lg" icon="bolt" onClick={() => setSheet('custom')}>
                    Extra practice
                  </Button>
                }
              />
            )}

            {node?.children.length ? (
              <section className={styles.section} aria-labelledby="subdecks-title">
                <div className={styles.sectionHead}>
                  <h2 className={styles.sectionTitle} id="subdecks-title">
                    Subdecks
                  </h2>
                </div>
                <DeckTree tree={node.children} counts={overview.counts} />
              </section>
            ) : null}
          </div>

          <div className={styles.deckSide}>
            <TileGrid columns={isDesktop ? 2 : undefined}>
              <ActionTile icon="plus" label="Add cards" to={`/add?deck=${deck.id}`} />
              <ActionTile icon="browse" label="See all cards" to={`/browse?deck=${deck.id}`} />
              <ActionTile icon="folder" label="New subdeck" onClick={() => setSheet('subdeck')} />
              <ActionTile icon="gauge" label="Study settings" onClick={() => setSheet('options')} />
            </TileGrid>

            {stats && stats.cards ? (
              <section className={styles.section} aria-labelledby="numbers-title">
                <div className={styles.sectionHead}>
                  <h2 className={styles.sectionTitle} id="numbers-title">
                    In this deck
                  </h2>
                </div>
                <div className={styles.stats}>
                  <Stat value={stats.cards} label="Cards" />
                  <Stat value={stats.known} label="Well known" hint="Remembered for 3+ weeks" />
                  <Stat value={stats.unseen} label="Not seen yet" />
                  {stats.paused ? <Stat value={stats.paused} label="Paused" /> : null}
                  {stats.flagged ? <Stat value={stats.flagged} label="Flagged" /> : null}
                </div>
              </section>
            ) : null}
          </div>
        </div>
      </div>

      <Sheet open={sheet === 'menu'} onClose={close} title={deck.name} subtitle="What would you like to do?">
        <ActionList>
          <ActionRow icon="edit" label="Rename or move" description="Change the name or put it inside another deck." onClick={() => setSheet('edit')} />
          <ActionRow icon="gauge" label="Study settings" description="Daily limits and how cards are scheduled." onClick={() => setSheet('options')} />
          <ActionRow icon="folder" label="New subdeck" description="Split this topic into smaller decks." onClick={() => setSheet('subdeck')} />
          <ActionRow icon="bolt" label="Extra practice" description="Study ahead, review mistakes, or cram a tag." onClick={() => setSheet('custom')} />
        </ActionList>
        <ActionList>
          <ActionRow
            icon="trash"
            danger
            label="Delete deck"
            description="Moves it and its subdecks to the trash. You can undo."
            onClick={remove}
          />
        </ActionList>
      </Sheet>
      <DeckFormSheet open={sheet === 'edit'} onClose={close} mode="edit" deck={deck} />
      <DeckFormSheet open={sheet === 'subdeck'} onClose={close} mode="create" parentId={deck.id} />
      <DeckOptionsSheet open={sheet === 'options'} onClose={close} deck={deck} />
      <CustomStudySheet open={sheet === 'custom'} onClose={close} deck={deck} />
    </div>
  );
}

function Stat({ value, label, hint }) {
  return (
    <div className={styles.stat} title={hint}>
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}
